import React, { useEffect, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger } from 'src/components/ui/select';
import { clsx } from 'clsx';

interface WidgetOption {
  id: string | null;
  displayName: string;
}

const initialOption: WidgetOption = {
  id: null,
  displayName: 'None',
};

// Generic over the form's values; forms with a resolver also pass their transformed values.
export const WebWidgetSelect = <T extends FieldValues, TTransformed extends FieldValues = T>({
  name = '' as Path<T>,
  label,
  control,
  options = [],
}: {
  name?: Path<T>;
  label: React.ReactNode;
  control: Control<T, unknown, TTransformed>;
  options?: WidgetOption[];
}) => {
  const [open, setOpen] = useState(false);
  const [selectOptions, setSelectOptions] = useState([initialOption]);

  useEffect(() => {
    if (options) {
      setSelectOptions([initialOption, ...options]);
    }
  }, [options]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        return (
          <label htmlFor={name}>
            {label}
            <Select
              defaultValue={value}
              open={open}
              onOpenChange={setOpen}
              onValueChange={onChange}
            >
              <SelectTrigger className="w-full bg-white border border-[#E3E6E8] rounded-sm-5">
                <div>
                  <p
                    className={clsx(
                      'text-sm font-medium',
                      selectOptions.find((option) => `${option.id}` === `${value}`) && 'text-ebony',
                    )}
                  >
                    <span>
                      {selectOptions.find((option) => `${option.id}` === `${value}`)?.displayName ||
                        'Choose widget'}
                    </span>
                  </p>
                </div>
              </SelectTrigger>
              <SelectContent className={'bg-light'}>
                {selectOptions.map((option) => {
                  return (
                    <SelectItem
                      className={'px-4 rounded-none bg-white hover:!bg-[#a498ff1a]'}
                      key={option.id}
                      value={option.id}
                      indicatorHidden
                    >
                      <div className="flex flex-col">
                        <div className="flex gap-2 items-center">
                          <p
                            className={clsx(
                              'text-sm text-start relative',
                              `${option.id}` === `${value}` ? 'font-semibold' : 'font-medium',
                            )}
                          >
                            <span>{option.displayName}</span>
                          </p>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </label>
        );
      }}
    />
  );
};
